import CryptoJS from 'crypto-js';

const SECRET_KEY = process.env.VUE_APP_USER_DATA_SECRET || 'ifood-user-data-2024-secret';

const CRYPTO_CONFIG = {
  version: '1.0',
  type: 'user_data'
};

class EncryptionService {
  
  /**
   * Gera hash seguro para verificação
   * @param {string} data - Dados para hash
   * @returns {string} - Hash SHA256
   */
  generateHash(data) {
    return CryptoJS.SHA256(data).toString();
  }

  /**
   * Criptografa dados de usuário
   * @param {Object} userData - Dados do usuário
   * @param {string} userData.uuid - ID único do usuário
   * @param {string} userData.name - Nome completo
   * @param {string} userData.cpf - CPF
   * @param {string} userData.email - E-mail
   * @param {string} userData.phone - Telefone (opcional)
   * @param {string} userData.region - Região (opcional)
   * @returns {string} - Dados criptografados em formato JSON
   */
  encryptUserData(userData) {
    try {
      const payload = {
        ...userData,
        timestamp: new Date().toISOString(),
        hash: this.generateHash(JSON.stringify(userData))
      };

      const payloadString = JSON.stringify(payload);
      const encrypted = CryptoJS.AES.encrypt(
        payloadString, 
        SECRET_KEY
      ).toString();

      return JSON.stringify({
        encrypted,
        version: CRYPTO_CONFIG.version,
        type: CRYPTO_CONFIG.type
      });

    } catch (error) {
      console.error('❌ Erro ao criptografar dados do usuário:', error);
      throw new Error('Falha na criptografia dos dados do usuário');
    }
  }

  /**
   * Descriptografa dados de usuário
   * @param {string} encryptedData - Dados criptografados (pode ser string JSON ou objeto)
   * @returns {Object|null} - Dados descriptografados ou null se falhar
   */
  decryptUserData(encryptedData) {
    try {
      let payload;
      
      if (typeof encryptedData === 'string') {
        try {
          payload = JSON.parse(encryptedData);
        } catch {
          payload = { encrypted: encryptedData };
        }
      } else {
        payload = encryptedData;
      }
      
      if (!payload.encrypted) {
        throw new Error('Formato de dados inválido');
      }

      const decrypted = CryptoJS.AES.decrypt(
        payload.encrypted, 
        SECRET_KEY
      );
      
      const originalText = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!originalText) {
        throw new Error('Falha na descriptografia');
      }

      return JSON.parse(originalText);

    } catch (error) {
      console.error('❌ Erro ao descriptografar dados do usuário:', error);
      return null;
    }
  }

  /**
   * Valida se os dados de usuário criptografados são válidos
   * @param {string} encryptedData - Dados criptografados
   * @returns {boolean} - True se válido, false caso contrário
   */
  validateUserData(encryptedData) {
    try {
      const decrypted = this.decryptUserData(encryptedData);
      
      if (!decrypted) return false;

      const requiredFields = ['uuid', 'name', 'cpf', 'timestamp'];
      const hasAllFields = requiredFields.every(field => decrypted[field]);
      
      if (!hasAllFields) return false;

      const timestamp = new Date(decrypted.timestamp);
      const now = new Date();
      const maxAge = 24 * 60 * 60 * 1000;
      
      if (now - timestamp > maxAge) {
        console.warn('⚠️ Token de usuário expirado');
        return false;
      }

      return true;

    } catch (error) {
      console.error('❌ Erro na validação:', error);
      return false;
    }
  }

}

export const encryptionService = new EncryptionService();
export default encryptionService;
